import { createAsyncThunk } from '@reduxjs/toolkit';

import api from '@setup/api';
import { handleServerError } from '@setup/config/serverErrorHandler';
import i18next from '@setup/locales/i18n';
import { NotificationService } from '@setup/notifications/SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';
import { StatusServerErrors } from '@shared/enums/status-server-errors';

import { isEmptyDataPetsWithFilter, removeCurrentPet, setCurrentPet, setLoadingPets } from './pets-slice';

import type { GetHubsParams, PetsInsurance, PetsVaccine, RootPetsData } from './pets-types';
import type { PetInsuranceRequestType } from '@compositeComponents/modals/hubs/pets/PetInsuranceModal/PetInsuranceContainer/utils/petInsuranceDataToRequest';
import type { PetVaccineRequestType } from '@compositeComponents/modals/hubs/pets/PetVaccineModal/VaccineContainer/utils/petVaccineDataToRequest';
import type { RootMeta } from '@pages/hubs/Garage/store/types';
import type { ErrorType, ServerErrorType } from '@shared/interfaces/error.interfaces';
import type { EntityHubSocket } from '@shared/models/hubs/hub-socket-response';
import type { AppDispatch, RootState } from '@store/store';

export const getPets = createAsyncThunk<
  { data: RootPetsData[]; meta: RootMeta },
  Partial<GetHubsParams>,
  { rejectValue: ErrorType; dispatch: AppDispatch; state: RootState }
>('getPets', async (petsParams, { dispatch, rejectWithValue }) => {
  dispatch(setLoadingPets({ isLoading: false }));
  try {
    const { data, meta } = await api.pets.getPets({
      page: petsParams.page || 1,
      ...petsParams,
    });
    dispatch(isEmptyDataPetsWithFilter({ filters: petsParams, petsData: data }));

    return {
      data,
      meta,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  } finally {
    dispatch(setLoadingPets({ isLoading: true }));
  }
});

export const createPet = createAsyncThunk<RootPetsData, Partial<RootPetsData>, { rejectValue: ErrorType }>(
  'createPet',
  async (data, { rejectWithValue }) => {
    try {
      return await api.pets.createPet(data);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
      return rejectWithValue(axiosError.data);
    }
  },
);
export const updatePet = createAsyncThunk<
  RootPetsData,
  { data: Partial<RootPetsData>; petID: number },
  { rejectValue: ErrorType }
>('updatePet', async ({ data, petID }, { rejectWithValue }) => {
  try {
    return await api.pets.updatePet(data, petID);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

export const removePet = createAsyncThunk<any, { petID: number }, { rejectValue: ErrorType }>(
  'removePet',
  async ({ petID }, { rejectWithValue, dispatch }) => {
    try {
      return await api.pets.removePet(petID).then(() => {
        dispatch(removeCurrentPet({ petID }));
        NotificationService.success(i18next.t('pets.notification.removePet'));
      });
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e);
      return rejectWithValue(axiosError.data);
    }
  },
);

export const getDraftPet = createAsyncThunk<RootPetsData, number, { rejectValue: ErrorType }>(
  'getDraftPet',
  async (petID, { rejectWithValue }) => {
    try {
      return await api.pets.getPet(petID);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(
        e,
        {
          [StatusServerErrors.AccessDenied]: {
            message: i18next.t('general.notifications.petNotSharedWithYou'),
          },
          [StatusServerErrors.ItemNotFound]: {
            message: i18next.t('general.notifications.petNotFound'),
          },
        },
        {
          notificationVariant: 'info',
        },
      );

      return rejectWithValue(axiosError.data);
    }
  },
);

export const getPet = createAsyncThunk<RootPetsData, number, { rejectValue: ErrorType }>(
  'getPet',
  async (petID, { rejectWithValue, dispatch }) => {
    try {
      const pet = await api.pets.getPet(petID);
      dispatch(setCurrentPet({ pet }));
      return pet;
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(
        e,
        {
          [StatusServerErrors.AccessDenied]: {
            message: i18next.t('general.notifications.petNotSharedWithYou'),
          },
          [StatusServerErrors.ItemNotFound]: {
            message: i18next.t('general.notifications.petNotFound'),
          },
        },
        {
          notificationVariant: 'info',
        },
      );

      return rejectWithValue(axiosError.data);
    }
  },
);

export const updatePetTempData = createAsyncThunk<
  any,
  {
    insurance_reminder?: { is_all_day: boolean; due_dated_at: string };
    petID: number;
  },
  { rejectValue: ErrorType }
>('updatePetTempData', async (data, { rejectWithValue }) => {
  try {
    const insurances = await api.pets.updatePetTempData(data);

    return insurances.data;
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

// ---------------- pet-insurance ----------------
export const getCurrentPetInsurance = createAsyncThunk<
  PetsInsurance,
  { insuranceId: number; petId: number },
  { rejectValue: ErrorType }
>('getCurrentPetInsurance', async ({ insuranceId, petId }, { rejectWithValue }) => {
  try {
    return await api.pets.getInsurance(insuranceId, petId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});
export const createPetInsurance = createAsyncThunk<
  PetsInsurance,
  (
    | PetInsuranceRequestType
    | Pick<
        PetInsuranceRequestType,
        'insurance_card_front' | 'policy_number' | 'effective' | 'expiration' | 'naic' | 'issued_by'
      >
  ) & { entity_id: number; entity_type: string; is_automatic_payment: boolean },
  { rejectValue: ErrorType }
>('createPetInsurance', async (data, { rejectWithValue }) => {
  try {
    return await api.pets.createInsurances(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});
export const updatePetInsurance = createAsyncThunk<
  PetsInsurance,
  Partial<PetInsuranceRequestType> & { insurancesId: number; is_automatic_payment: boolean },
  { rejectValue: ErrorType }
>('updatePetInsurance', async (data, { rejectWithValue }) => {
  try {
    return await api.pets.updateInsurances(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });

    return rejectWithValue(axiosError.data);
  }
});
export const deletePetInsurance = createAsyncThunk<
  any,
  { petID: number; insuranceID: number },
  { rejectValue: ErrorType }
>('deletePetInsurance', async ({ petID, insuranceID }, { rejectWithValue }) => {
  try {
    return await api.pets.deleteInsurance(petID, insuranceID).then(() => insuranceID);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});
// ---------------- pet-vaccine ----------------
export const getCurrentPetVaccine = createAsyncThunk<
  PetsVaccine,
  { vaccineId: number; petId: number },
  { rejectValue: ErrorType }
>('getCurrentPetVaccine', async ({ vaccineId, petId }, { rejectWithValue }) => {
  try {
    return await api.pets.getVaccine(vaccineId, petId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});
export const createPetVaccine = createAsyncThunk<
  PetsVaccine,
  PetVaccineRequestType & { entity_id: number; entity_type: string },
  { rejectValue: ErrorType }
>('createPetVaccine', async (data, { rejectWithValue }) => {
  try {
    return await api.pets.createVaccine(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});
export const updatePetVaccine = createAsyncThunk<
  PetsVaccine,
  Partial<PetVaccineRequestType> & { vaccineId: number },
  { rejectValue: ErrorType }
>('updatePetVaccine', async (data, { rejectWithValue }) => {
  try {
    return await api.pets.updateVaccine(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });

    return rejectWithValue(axiosError.data);
  }
});
export const deletePetVaccine = createAsyncThunk<
  any,
  { petId: number; vaccineId: number },
  { rejectValue: ErrorType }
>('deletePetVaccine', async ({ petId, vaccineId }, { rejectWithValue }) => {
  try {
    return await api.pets.deleteVaccine(petId, vaccineId).then(() => vaccineId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

// ---------------- web-socket ----------------
export const updatePetWS = createAsyncThunk<
  {
    petWS: RootPetsData;
  },
  EntityHubSocket,
  { rejectValue: ErrorType }
>('updatePetWS', async (socketEvent, { rejectWithValue }) => {
  try {
    const updatePetResponse = await api.pets.getPet(socketEvent.entity_id);

    return {
      petWS: updatePetResponse,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const addPetWS = createAsyncThunk<
  {
    addPetWS: RootPetsData;
  },
  EntityHubSocket,
  { rejectValue: ErrorType }
>('addPetWS', async (socketEvent, { rejectWithValue }) => {
  try {
    const addPetResponse = await api.pets.getPet(socketEvent.entity_id);

    return {
      addPetWS: addPetResponse,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const acceptedPetWS = createAsyncThunk<
  {
    acceptedPetWS: RootPetsData;
  },
  number,
  { rejectValue: ErrorType }
>('acceptedPetWS', async (petID, { rejectWithValue }) => {
  try {
    const acceptedPetResponse = await api.pets.getPet(petID);

    return {
      acceptedPetWS: acceptedPetResponse,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});
